import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { CheckCircle2, Clock, FileText, X, XCircle } from 'lucide-react';
import type { BlockHoursStart, PublicQuoteView } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { QuoteDocumentView } from '../components/QuoteDocumentView.tsx';
import { QuoteDepositForm } from '../components/QuoteDepositForm.tsx';
import { CardSetupForm } from '../components/CardSetupForm.tsx';

/** `YYYY-MM-DD` as "November 1, 2026", without a timezone shifting the day. */
const longDate = (iso: string): string =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

// The client-facing quote. No login, no nav, no T3 app around it — the
// person reading this may never have seen the portal and may not be a
// contact on the account at all. Everything on screen comes from the
// frozen snapshot taken when the quote was sent, so what is signed is
// exactly what was sent.

const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px] text-[var(--color-body)] outline-none transition focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

function Shell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="min-h-screen bg-[var(--color-surface)] px-4 py-10 sm:py-14">
      <div className="mx-auto w-full max-w-3xl">
        {children}
        <p className="mt-6 text-center text-xs text-[var(--color-muted)]">
          Questions about this quote? Reply to the email it came in and we will pick it up.
        </p>
      </div>
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white p-6 shadow-sm sm:p-8">
      {children}
    </div>
  );
}

/** The states where there is nothing to sign — each says what to do next. */
function Closed({
  icon,
  title,
  message,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
}): React.ReactElement {
  return (
    <Panel>
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-surface)]">
          {icon}
        </div>
        <h1 className="text-xl font-bold text-[var(--color-header)]">{title}</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-[var(--color-muted)]">{message}</p>
      </div>
    </Panel>
  );
}

const money = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Where the Accept button stood, once the quote is signed. */
function SignedNotice({ signerName, decidedAt }: { signerName: string; decidedAt: string }): React.ReactElement {
  return (
    <div className="flex w-full max-w-[520px] items-start gap-3 rounded-[8px] bg-white p-5 text-left">
      <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" aria-hidden="true" />
      <div>
        <p className="text-[18px] font-semibold text-[#47535D]">Accepted - thank you</p>
        <p className="mt-1 text-[15px] leading-relaxed text-[#47535D]">
          Signed by {signerName} on {new Date(decidedAt).toLocaleDateString()}. We've emailed you a
          copy, and your account manager will be in touch to schedule the work.
        </p>
      </div>
    </div>
  );
}

export function PublicQuotePage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicQuoteView | null>(null);
  const [consentText, setConsentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [consented, setConsented] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Which plan the client has picked, on a quote that offers them.
  // Nothing is preselected on purpose: a default here is a number the
  // client did not choose, on a document they are about to sign.
  const [planId, setPlanId] = useState<string | null>(null);
  // The signing dialog, opened by either Accept button.
  const [signing, setSigning] = useState(false);
  // Accept was pressed before a plan was chosen; the total says so.
  const [planPrompt, setPlanPrompt] = useState(false);
  // Block hours in the first two weeks of a month: start now (charged
  // today) or on the 1st. Not preselected, for the same reason as the plan:
  // it decides what is charged today.
  const [start, setStart] = useState<BlockHoursStart | null>(null);

  useDocumentTitle(quote?.snapshot?.title ?? 'Your quote');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNotFound(false);
    try {
      const data = await apiFetch<{ quote: PublicQuoteView; consentText: string }>(
        `/api/quotes/${token}`,
      );
      setQuote(data.quote);
      setConsentText(data.consentText);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true);
      else setLoadError('We could not load this quote. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  // A quote that offers plans cannot be signed until one is picked. The
  // server refuses it anyway (plan_required); this is so the page says
  // so before the client fills in their name rather than after.
  const plansOffered = quote?.snapshot?.planOptions ?? [];
  const planMissing = plansOffered.length > 0 && !planId;
  const startOffered = plansOffered.length > 0 && Boolean(quote?.startNowOn) && quote?.state === 'live';
  const startMissing = startOffered && !start;

  async function decide(
    decision: 'accept' | 'decline',
    paymentIntentId?: string,
    setupIntentId?: string,
  ): Promise<void> {
    setBusy(true);
    setFormError(null);
    try {
      await apiFetch(`/api/quotes/${token}/${decision}`, {
        method: 'POST',
        json: {
          signerName: signerName.trim(),
          signerEmail: signerEmail.trim(),
          consented: decision === 'accept' ? consented : undefined,
          declineReason: decision === 'decline' ? declineReason.trim() || null : null,
          paymentIntentId: paymentIntentId ?? null,
          // The card saved for monthly billing, on a block-hours quote.
          // Checked with Stripe server-side, like the deposit.
          setupIntentId: setupIntentId ?? null,
          // An id only. The server reads the price back out of the
          // frozen snapshot rather than trusting anything sent here.
          planId,
          // Only where the choice was offered; the server checks the date.
          start: startOffered ? start : null,
        },
      });
      // Re-read rather than patching state locally: the server decides
      // what this link is now, and a refresh must show the same thing.
      setSigning(false);
      await load();
    } catch (e) {
      setFormError(
        e instanceof ApiError
          ? e.message
          : 'Something went wrong. Please try again, or reply to the email that brought you here.',
      );
      // A 409 means the link changed underneath us (already signed,
      // revised, expired). Reload so the page stops offering an action
      // that cannot succeed.
      if (e instanceof ApiError && e.status === 409) {
        setSigning(false);
        await load();
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Shell>
        <Panel>
          <p className="text-center text-sm text-[var(--color-muted)]">Loading your quote…</p>
        </Panel>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <Closed
          icon={<FileText className="h-6 w-6 text-[var(--color-muted)]" aria-hidden="true" />}
          title="Quote not found"
          message="This link doesn't match a quote. It may have been mistyped — check the link in your email, or reply to it and we'll send a fresh one."
        />
      </Shell>
    );
  }

  if (loadError || !quote) {
    return (
      <Shell>
        <Panel>
          <p className="text-center text-sm text-[var(--color-danger)]">
            {loadError ?? 'We could not load this quote.'}
          </p>
          <div className="mt-4 text-center">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </Panel>
      </Shell>
    );
  }

  if (quote.state === 'expired') {
    return (
      <Shell>
        <Closed
          icon={<Clock className="h-6 w-6 text-[var(--color-muted)]" aria-hidden="true" />}
          title="This quote has expired"
          message={`It was valid until ${quote.expiresOn}. Reply to the email it came in and we'll send you an updated quote.`}
        />
      </Shell>
    );
  }

  if (quote.state === 'superseded') {
    return (
      <Shell>
        <Closed
          icon={<FileText className="h-6 w-6 text-[var(--color-muted)]" aria-hidden="true" />}
          title="This quote has been revised"
          message="A newer version of this quote has been sent to you. Please use the most recent link — if you can't find it, reply to the email and we'll resend it."
        />
      </Shell>
    );
  }

  if (quote.state === 'declined') {
    return (
      <Shell>
        <Closed
          icon={<XCircle className="h-6 w-6 text-[var(--color-muted)]" aria-hidden="true" />}
          title="This quote was declined"
          message={`Recorded${quote.decision ? ` for ${quote.decision.signerName}` : ''}. If that wasn't intended, reply to the email that brought you here and we'll sort it out.`}
        />
      </Shell>
    );
  }

  const accepted = quote.state === 'accepted';
  const live = quote.state === 'live';
  const chosenPlan = plansOffered.find((p) => p.id === planId) ?? null;

  // Accept with no plan chosen goes to the plans rather than to a form
  // that would only refuse: the total says what is missing.
  function startAccept(): void {
    if (planMissing || startMissing) {
      setPlanPrompt(true);
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setDeclining(false);
    setFormError(null);
    setSigning(true);
  }

  return (
    <>
      {quote.snapshot && (
        <QuoteDocumentView
          quote={{
            ...quote.snapshot,
            depositCents: quote.depositCents,
            balanceCents: quote.balanceCents,
            // Only while it can still be signed: a signed quote saved its card already.
            cardRequired: live && quote.cardRequired,
            firstChargeOn: quote.firstChargeOn,
            startNowOn: live ? quote.startNowOn : null,
          }}
          start={start}
          onStartChange={
            live
              ? (value) => {
                  setStart(value);
                  if (planId) setPlanPrompt(false);
                }
              : undefined
          }
          // A signed ladder shows the rung that was signed for.
          selectedPlanId={accepted ? (quote.decision?.selectedPlanId ?? null) : planId}
          onSelectPlan={
            live
              ? (id) => {
                  setPlanId(id);
                  if (id && (!startOffered || start)) setPlanPrompt(false);
                }
              : undefined
          }
          onAccept={live ? startAccept : undefined}
          planPrompt={planPrompt}
          acceptSlot={
            accepted && quote.decision ? (
              <SignedNotice
                signerName={quote.decision.signerName}
                decidedAt={quote.decision.decidedAt}
              />
            ) : undefined
          }
        />
      )}

      {signing && live && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-10"
          role="dialog"
          aria-modal="true"
          aria-label={declining ? 'Decline this quote' : 'Accept this quote'}
          onClick={() => !busy && setSigning(false)}
        >
          <div
            className="w-full max-w-[560px] rounded-[8px] bg-white p-6 shadow-xl sm:p-8"
            style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-[22px] font-bold text-[#47535D]">
                {declining ? 'Decline this quote' : 'Accept this quote'}
              </h2>
              <button
                type="button"
                onClick={() => setSigning(false)}
                disabled={busy}
                className="rounded p-1 text-[#47535D] hover:bg-black/5"
                aria-label="Close"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            {!declining && chosenPlan && (
              <p className="mt-3 rounded-[6px] bg-[#E0E8EC] px-4 py-3 text-[15px] text-[#47535D]">
                <span className="font-semibold">{chosenPlan.name}</span> -{' '}
                {money(chosenPlan.monthlyPriceUsd)} / month
              </p>
            )}
            {quote.expiresOn && (
              <p className="mt-3 text-sm text-[var(--color-muted)]">
                This quote is valid until <strong>{quote.expiresOn}</strong>.
              </p>
            )}

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                  Your full name
                </span>
                <input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className={inputClass}
                  autoComplete="name"
                  placeholder="Alex Morgan"
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                  Your email
                </span>
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  className={inputClass}
                  autoComplete="email"
                  placeholder="alex@company.com"
                />
              </label>
            </div>

            {declining ? (
              <label className="mt-4 block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                  Anything you'd like us to know?{' '}
                  <span className="font-normal text-[var(--color-muted)]">(optional)</span>
                </span>
                <textarea
                  rows={3}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  className={inputClass}
                  placeholder="Budget, timing, scope - whatever is useful."
                />
              </label>
            ) : (
              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(e) => setConsented(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#54387E]"
                />
                <span className="text-sm text-[var(--color-body)]">{consentText}</span>
              </label>
            )}

            {formError && <p className="mt-3 text-sm text-[var(--color-danger)]">{formError}</p>}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {declining ? (
                <>
                  <Button
                    variant="danger"
                    onClick={() => void decide('decline')}
                    disabled={busy || !signerName.trim() || !signerEmail.trim()}
                  >
                    {busy ? 'Sending…' : 'Decline quote'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDeclining(false);
                      setFormError(null);
                    }}
                    disabled={busy}
                  >
                    Back
                  </Button>
                </>
              ) : (
                <>
                  {/* When a deposit is due, paying IS accepting: the card
                      is confirmed first and only a succeeded payment
                      produces the intent id that records the signature.
                      A declined card leaves nothing behind. */}
                  {quote.depositCents > 0 ? (
                    <div className="w-full">
                      <QuoteDepositForm
                        token={token!}
                        depositCents={quote.depositCents}
                        balanceCents={quote.balanceCents}
                        disabled={
                          busy || planMissing || !signerName.trim() || !signerEmail.trim() || !consented
                        }
                        onPaid={(paymentIntentId) => decide('accept', paymentIntentId)}
                      />
                    </div>
                  ) : quote.cardRequired && chosenPlan ? (
                    // Block hours: nothing is charged today, but the card
                    // for monthly billing is saved as part of signing.
                    <div className="w-full">
                      <CardSetupForm
                        startUrl={`/api/quotes/${token}/card-setup`}
                        summary={
                          <>
                            {start === 'now' ? (
                              <>
                                <span className="font-semibold">
                                  {money(chosenPlan.monthlyPriceUsd)} is charged today
                                </span>{' '}
                                for this month, then on the 1st of each month from{' '}
                                {quote.firstChargeOn ? longDate(quote.firstChargeOn) : 'next month'}.
                              </>
                            ) : (
                              <>
                                <span className="font-semibold">Nothing is charged today.</span>{' '}
                                {money(chosenPlan.monthlyPriceUsd)} is charged on{' '}
                                {quote.firstChargeOn ? longDate(quote.firstChargeOn) : 'the 1st of next month'}, then on
                                the 1st of each month.
                              </>
                            )}
                          </>
                        }
                        buttonLabel={start === 'now' ? 'Pay and accept' : 'Save card and accept'}
                        disabled={busy || !signerName.trim() || !signerEmail.trim() || !consented}
                        onSaved={(setupIntentId) => decide('accept', undefined, setupIntentId)}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void decide('accept')}
                      disabled={busy || planMissing || !signerName.trim() || !signerEmail.trim() || !consented}
                      className="h-11 rounded-[4px] bg-[#54387E] px-6 text-[16px] font-semibold text-white transition hover:brightness-110 disabled:opacity-50 disabled:hover:brightness-100"
                    >
                      {busy ? 'Recording…' : 'Accept and sign'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setDeclining(true);
                      setFormError(null);
                    }}
                    disabled={busy}
                    className="text-sm text-[var(--color-muted)] underline-offset-2 hover:text-[var(--color-header)] hover:underline disabled:opacity-50"
                  >
                    No thanks - decline
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
